import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
} from '@chakra-ui/react';
import { Avatar } from '../components/Avatar';
import { EyeOff, HeartHandshake, Home, Lock } from 'lucide-react';
import {
  MadeInIndia,
  ReadingColumn,
  SiteShell,
  richText,
} from '../components/site/SiteChrome';
import { SITE, type TeamMember } from '../content/site';
import { useLanguage, useT } from '../i18n';

// About us (M42 addendum, docs/landing-page.md D118). Only what's true and
// public: the principles the app keeps, and Paperdrop as it describes itself.

type Translate = ReturnType<typeof useT>;

function principles(
  t: Translate
): Array<{ icon: ReactNode; title: string; body: ReactNode }> {
  const link = (to: string, text: string) => (
    <Link as={RouterLink} to={to} color="accent" fontWeight={600}>
      {text}
    </Link>
  );
  return [
    {
      icon: <Lock size={22} />,
      title: t('site.about.private.title'),
      body: richText(t('site.about.private.body'), {
        home: link('/', t('site.about.private.homeLink')),
        privacy: link('/privacy', t('site.link.privacyPolicy')),
      }),
    },
    {
      icon: <EyeOff size={22} />,
      title: t('site.about.noAds.title'),
      body: t('site.about.noAds.body'),
    },
    {
      icon: <HeartHandshake size={22} />,
      title: t('site.about.open.title'),
      body: richText(t('site.about.open.body'), {
        link: link('/pricing', t('site.about.open.link')),
      }),
    },
    {
      icon: <Home size={22} />,
      title: t('site.about.families.title'),
      body: t('site.about.families.body'),
    },
  ];
}

/** The people behind Twinden (SITE.team), each in their own words. */
export function TeamCards({ members }: { members: TeamMember[] }) {
  // Hindi role and bio when given (D146); names stay as they are.
  const hindi = useLanguage() === 'hi';
  if (members.length === 0) return null;
  return (
    <SimpleGrid
      columns={{ base: 1, md: members.length > 1 ? 2 : 1 }}
      spacing={4}
    >
      {members.map((member) => (
        <Box
          key={member.name}
          bg="surface"
          borderWidth="1px"
          borderColor="line"
          borderRadius="card"
          p={{ base: 5, md: 6 }}
        >
          <VStack align="stretch" spacing={4}>
            <HStack spacing={4}>
              <Avatar size="lg" name={member.name} src={member.photo} />
              <Box>
                <Text fontWeight={800} fontSize="20px">
                  {member.name}
                </Text>
                <Text color="muted" fontSize="15px">
                  {(hindi && member.hi?.role) || member.role}
                </Text>
              </Box>
            </HStack>
            <Text color="ink.body" lineHeight="26px" whiteSpace="pre-line">
              {(hindi && member.hi?.bio) || member.bio}
            </Text>
          </VStack>
        </Box>
      ))}
    </SimpleGrid>
  );
}

export function AboutPage() {
  const t = useT();
  return (
    <SiteShell>
      <ReadingColumn>
        <VStack align="stretch" spacing={10}>
          <VStack align="stretch" spacing={4}>
            <Heading
              as="h1"
              fontSize={{ base: '36px', md: '48px' }}
              lineHeight={1.05}
            >
              {t('site.about.title')}
            </Heading>
            <Text fontSize="20px" lineHeight="30px" color="ink.body">
              {t('site.about.intro')}
            </Text>
          </VStack>

          <Box as="section">
            <Heading as="h2" fontSize="26px" pb={4}>
              {t('site.about.standFor')}
            </Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
              {principles(t).map((principle) => (
                <Box
                  key={principle.title}
                  bg="surface"
                  borderWidth="1px"
                  borderColor="line"
                  borderRadius="card"
                  p={5}
                >
                  <Flex
                    w="44px"
                    h="44px"
                    borderRadius="14px"
                    bg="accent.tint"
                    color="accent"
                    align="center"
                    justify="center"
                    mb={4}
                  >
                    {principle.icon}
                  </Flex>
                  <Heading as="h3" fontSize="19px" pb={2}>
                    {principle.title}
                  </Heading>
                  <Text color="ink.body" lineHeight="24px">
                    {principle.body}
                  </Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>

          <Box as="section">
            <Heading as="h2" fontSize="26px" pb={3}>
              {t('site.about.why.title')}
            </Heading>
            <Text color="ink.body" lineHeight="26px" pb={3}>
              {t('site.about.why.body')}
            </Text>
            <MadeInIndia long />
          </Box>

          <Box as="section">
            <Heading as="h2" fontSize="26px" pb={4}>
              {t('site.about.who.title')}
            </Heading>
            {SITE.team.length > 0 && (
              <Box pb={4}>
                <TeamCards members={SITE.team} />
              </Box>
            )}
            <Box
              bg="surface"
              borderWidth="1px"
              borderColor="line"
              borderRadius="card"
              p={{ base: 5, md: 6 }}
            >
              <VStack align="stretch" spacing={4}>
                <HStack spacing={3}>
                  <Image
                    src="/brand/paperdrop-mark.png"
                    alt=""
                    boxSize="40px"
                  />
                  <Box>
                    <Text fontWeight={800} fontSize="20px">
                      {SITE.company}
                    </Text>
                    <Text color="muted" fontSize="15px">
                      {SITE.contact.address}
                    </Text>
                  </Box>
                </HStack>
                <Text color="ink.body" lineHeight="26px">
                  {t('site.about.who.company', { company: SITE.company })}
                </Text>
                <Link
                  href={SITE.companyUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="accent"
                  fontWeight={700}
                  alignSelf="flex-start"
                >
                  {t('site.about.who.visit', {
                    site: SITE.companyUrl.replace(/^https?:\/\//, ''),
                  })}
                </Link>
              </VStack>
            </Box>
          </Box>

          <Box as="section">
            <Heading as="h2" fontSize="26px" pb={3}>
              {t('site.about.contact.title')}
            </Heading>
            <Text color="ink.body" lineHeight="26px" pb={5}>
              {richText(t('site.about.contact.body'), {
                email: (
                  <Link
                    href={`mailto:${SITE.contact.email}`}
                    color="accent"
                    fontWeight={600}
                  >
                    {SITE.contact.email}
                  </Link>
                ),
              })}
            </Text>
            <HStack spacing={3} flexWrap="wrap">
              <Button as={RouterLink} to="/request-invite" h="52px" px={7}>
                {t('site.requestInvite.title')}
              </Button>
              <Button
                as={RouterLink}
                to="/contact"
                variant="outline"
                colorScheme="gray"
                h="52px"
                px={6}
              >
                {t('site.nav.contact')}
              </Button>
            </HStack>
            {/* Third-party notices (the emoji names, M69): served as a file. */}
            <Link
              href="/licenses.txt"
              // A plain text file: a new tab, so the app stays where it was.
              target="_blank"
              rel="noopener"
              display="inline-block"
              mt={5}
              fontSize="15px"
              color="muted"
              textDecoration="underline"
            >
              {t('site.about.licenses')}
            </Link>
          </Box>
        </VStack>
      </ReadingColumn>
    </SiteShell>
  );
}
